import { createApi } from '@reduxjs/toolkit/query/react';
import { downloadBlobResponse } from 'src/app-redux/rtkq.util';
import type { Booking } from 'src/booking/Booking';
import { apiResources } from 'src/common/Constants';
import { filterObject } from 'src/common/utils/object.util';
import { getLoggedInUser, isShipperUser } from 'src/management/User';
import { ShipmentPoolTypes, type FetchShipmentsArgs, type NewShipment, type Shipment } from 'src/shipment/Shipment';
import { baseQueryWithAuth, transformResponses } from '../rtkq.interceptor';

const shipmentsTag = 'shipments';

/** RTK Query API slice for shipment-related endpoints. */
export const shipmentsApi = createApi({
  reducerPath: 'shipmentsApi',
  tagTypes: [shipmentsTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch shipments with bookings and filters. */
    getShipmentsWithBookings: builder.query<Array<Shipment & { bidIds?: number[] }>, FetchShipmentsArgs>({
      providesTags: [shipmentsTag],
      query: (arg) => {
        const user = getLoggedInUser();
        const shipper = isShipperUser(user) ? `/shipper/${user.id}` : '';

        const {
          assignedToMe,
          createdByMe,
          endDate,
          page,
          pageSize,
          privateLoadboard,
          referenceNumber,
          searchParam,
          startDate,
          types,
          ...rest
        } = arg;
        const paramsObject = {
          filter: types?.toString(),
          filterAssignedToMe: assignedToMe,
          filterCreatedByMe: createdByMe,
          fromDate: startDate,
          page: page - 1,
          pageSize: pageSize || 5,
          poolType: privateLoadboard ? ShipmentPoolTypes.PRIVATE : null,
          private: privateLoadboard,
          referenceNo: referenceNumber,
          toDate: endDate,
          ...rest,
          ...searchParam,
        };

        const params = filterObject(paramsObject);
        return { url: `${apiResources.shipment}${shipper}/v3`, params };
      },
      ...transformResponses,
    }),
    /** Query to fetch a single shipment by ID. */
    getShipment: builder.query<Shipment & { bidIds?: number[] }, number>({
      providesTags: [shipmentsTag],
      query: (id) => `${apiResources.shipment}/${id}/v3`,
      ...transformResponses,
    }),
    /** Query to fetch bookings for a shipment by shipment ID. */
    getShipmentBookings: builder.query<Booking[], number>({
      providesTags: [shipmentsTag],
      query: (shipmentId) => `${apiResources.shipment}/${shipmentId}/booking/v3`,
      transformResponse: (baseQueryReturnValue: { data: { bookingResponseV3Dtos: Booking[] } }) =>
        transformResponses.transformResponse(baseQueryReturnValue).bookingResponseV3Dtos,
      transformErrorResponse: transformResponses.transformErrorResponse,
    }),
    /** Mutation to create a new shipment. */
    createShipment: builder.mutation<Shipment, NewShipment>({
      query: (body) => ({ url: `${apiResources.shipment}/v3`, method: 'POST', body }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [shipmentsTag]),
    }),
    /** Mutation to update a shipment by ID. */
    updateShipment: builder.mutation<string, Shipment>({
      query: ({ shipmentId, ...patch }) => ({
        url: `${apiResources.shipment}/${shipmentId}/update`,
        method: 'PATCH',
        body: patch,
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [shipmentsTag]),
    }),
    /** Mutation to cancel a shipment by ID and reason. */
    cancelShipment: builder.mutation<
      {
        id: number;
        weight: number;
        shipperId: number;
        shipperName: string;
        companyId: number;
        companyName: string;
      },
      { shipmentId: number; cancellationReason: string }
    >({
      query: ({ shipmentId, cancellationReason }) => ({
        url: `${apiResources.shipment}/${shipmentId}/cancel`,
        method: 'POST',
        body: { distanceInKilometers: 0, timeInHours: 0, cancellationType: 1, cancellationReason },
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [shipmentsTag]),
    }),
    /** Mutation to upload a CSV file for correcting shipments. */
    correctShipments: builder.mutation<void, File>({
      query: (csv) => {
        const formData = new FormData();
        formData.append('file', csv);
        return {
          url: `${apiResources.shipment}/contract-amount/upload`,
          method: 'POST',
          body: formData,
          responseHandler: (response) => downloadBlobResponse(response, csv.name),
        };
      },
    }),
    /** Mutation to bulk upload shipments. */
    bulkShipments: builder.mutation<void, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${apiResources.shipment}/bulk-upload`,
          method: 'POST',
          body: formData,
          responseHandler: (response) => downloadBlobResponse(response, file.name),
        };
      },
    }),
  }),
});

/** RTK Query hooks for shipment-related endpoints. */
export const {
  useGetShipmentsWithBookingsQuery,
  useGetShipmentQuery,
  useLazyGetShipmentQuery,
  useGetShipmentBookingsQuery,
  useCreateShipmentMutation,
  useUpdateShipmentMutation,
  useCancelShipmentMutation,
  useCorrectShipmentsMutation,
  useBulkShipmentsMutation,
} = shipmentsApi;
